import { ArrowLeft, ChevronDown, ChevronRight, Pause, Play, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../auth-context';
import { formatTashkentDate, formatTashkentWeekday, t } from '../../i18n';
import type {
  ChildHistoryDay,
  ChildHistoryRecitation,
  ChildProfileResponse,
  PrayerTime,
} from '../../types';
import { AppShell } from '../shared/AppShell';
import { Avatar } from '../shared/Avatar';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtTotalMinutes(sec: number): string {
  return String(Math.round(sec / 60));
}

function fmtDayHeader(quranDay: string): string {
  return `${formatTashkentWeekday(quranDay)} · ${formatTashkentDate(quranDay)}`;
}

export function ChildProfile() {
  const navigate = useNavigate();
  const { state } = useAuth();
  const child = state.status === 'child' ? state.child : null;

  const [profile, setProfile] = useState<ChildProfileResponse | null>(null);
  const [days, setDays] = useState<ChildHistoryDay[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    api.myProfile().then(setProfile);
    api.myHistory({ limit: 14 }).then((res) => {
      setDays(res.days);
      setCursor(res.next_cursor);
      const today = res.days[0]?.quran_day;
      if (today) setExpanded(new Set([today]));
    });
    if (!audioRef.current) {
      const a = document.createElement('audio');
      a.preload = 'metadata';
      a.style.display = 'none';
      document.body.appendChild(a);
      audioRef.current = a;
      a.addEventListener('play', () => setPlaying(true));
      a.addEventListener('pause', () => setPlaying(false));
      a.addEventListener('ended', () => setPlaying(false));
    }
    return () => {
      audioRef.current?.pause();
      setActiveId(null);
      setPlaying(false);
    };
  }, []);

  if (!child) return null;

  const toggleDay = (day: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const playToggle = (item: ChildHistoryRecitation) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (activeId === item.id) {
      if (audio.paused) void audio.play();
      else audio.pause();
      return;
    }
    setActiveId(item.id);
    audio.src = item.audio_url;
    void audio.play().catch(() => setPlaying(false));
  };

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.myHistory({ limit: 14, cursor });
      setDays((prev) => (prev ? [...prev, ...res.days] : res.days));
      setCursor(res.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <AppShell ramp={child.color_ramp}>
      <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-5 p-5">
        <header className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label={t('common.back')}
            className="text-ink-muted active:scale-[0.96]"
          >
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[18px] font-medium">{t('profile.title')}</h1>
        </header>

        {profile ? (
          <Card variant="child">
            <div className="flex items-center gap-4">
              <Avatar
                initial={profile.child.avatar_initial}
                ramp={profile.child.color_ramp}
                size="lg"
                src={profile.child.avatar_url ?? undefined}
              />
              <div className="flex flex-col">
                <span className="text-[16px] font-medium">{profile.child.name}</span>
                <span className="text-[26px] font-medium leading-tight">
                  {profile.streak_days > 0
                    ? t('profile.streak.value', { n: profile.streak_days })
                    : t('profile.streak.zero')}
                </span>
              </div>
            </div>
          </Card>
        ) : (
          <Card variant="child">
            <div className="h-[90px] animate-pulse rounded bg-child-text/10" />
          </Card>
        )}

        {profile ? (
          <section className="grid grid-cols-3 gap-2">
            <StatTile
              label={t('profile.stat.week')}
              value={String(profile.week.recitations)}
              hint={t('profile.stat.week.hint', { n: profile.week.days_active })}
            />
            <StatTile
              label={t('profile.stat.month')}
              value={String(profile.month.recitations)}
              hint={t('profile.stat.recitations')}
            />
            <StatTile
              label={t('profile.stat.minutes')}
              value={fmtTotalMinutes(profile.totals.duration_seconds)}
              hint={t('profile.stat.minutes.hint')}
            />
          </section>
        ) : null}

        {profile && profile.surahs_completed.length > 0 ? (
          <section className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
              <Sparkles size={12} />
              {t('profile.surahs.title', { n: profile.surahs_completed.length })}
            </div>
            <ul className="flex flex-wrap gap-1.5">
              {profile.surahs_completed.map((s) => (
                <li
                  key={s.surah_number}
                  className="rounded-sm bg-success-bg px-2.5 py-1 text-[12px] text-success"
                >
                  {s.surah_number}. {s.surah_name_uz}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="flex flex-col gap-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
            {t('profile.history.title')}
          </div>

          {days === null ? (
            <p className="text-[13px] text-ink-muted">{t('common.loading')}</p>
          ) : days.length === 0 ? (
            <p className="text-[13px] text-ink-muted">{t('profile.history.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {days.map((d) => {
                const isOpen = expanded.has(d.quran_day);
                return (
                  <li key={d.quran_day}>
                    <button
                      type="button"
                      onClick={() => toggleDay(d.quran_day)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center gap-2 rounded-md bg-surface-muted px-3 py-2.5 text-left active:scale-[0.99]"
                    >
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span className="flex-1 text-[13px]">{fmtDayHeader(d.quran_day)}</span>
                      <span className="text-[12px] text-ink-muted tabular-nums">
                        {t('profile.history.count', { n: d.total_recitations })}
                      </span>
                    </button>
                    {isOpen ? (
                      <div className="mt-2 flex flex-col gap-3 px-1">
                        {d.prayers.map((g) => (
                          <div key={g.prayer_time} className="flex flex-col gap-1.5">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
                              {t(PRAYER_NAME_KEY[g.prayer_time])}
                            </div>
                            <ul className="flex flex-col gap-1.5">
                              {g.recitations.map((r, idx) => {
                                const isPlaying = activeId === r.id && playing;
                                return (
                                  <li
                                    key={r.uuid}
                                    className="flex items-center gap-3 rounded-md border-hairline border-border bg-surface px-3 py-2"
                                  >
                                    <button
                                      onClick={() => playToggle(r)}
                                      aria-label={isPlaying ? 'pause' : 'play'}
                                      className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-ink active:scale-[0.97]"
                                    >
                                      {isPlaying ? (
                                        <Pause size={14} />
                                      ) : (
                                        <Play size={14} className="ml-0.5" />
                                      )}
                                    </button>
                                    <div className="flex flex-1 flex-col text-[13px]">
                                      <span>
                                        {idx + 1}-marta · {r.surah.number}. {r.surah.name_uz}
                                      </span>
                                      <span className="text-[11px] text-ink-muted tabular-nums">
                                        {fmtDuration(r.duration_seconds)}
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          {cursor ? (
            <Button variant="ghost" fullWidth onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t('common.loading') : t('profile.history.more')}
            </Button>
          ) : null}
        </section>
      </main>
    </AppShell>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="flex flex-col items-start gap-0.5 rounded-md border-hairline border-border bg-surface px-3 py-2.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-ink-subtle">
        {label}
      </span>
      <span className="text-[22px] font-medium leading-none tabular-nums">{value}</span>
      <span className="text-[10px] text-ink-muted">{hint}</span>
    </div>
  );
}
